import * as React from "react";
import {Breadcrumb, Button, ControlLabel, FormControl, FormGroup} from "react-bootstrap";
import {FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ControlRoomTable} from "../table/ControlRoomTable";
import {WithApi, WithApiProperties} from "../../util/WithApi";
import {DirectoryItem} from "../../../model";
import {DirectoryItemsColumnFactory} from "./DirectoryItemsColumnFactory";
import {ChooseFileStrategy} from "./strategies/ChooseFileStrategy";
import {ChooseLocalFileStrategy} from "./strategies/ChooseLocalFileStrategy";
import {ChooseLocalOrNetworkFileStrategy} from "./strategies/ChooseLocalOrNetworkFileStrategy";
import {Logger} from "../../util/Logger";

export enum ChooseFileFormSourceType {
  Local,
  LocalAndNetwork,
  S3,
}

export enum ChooseMode {
  FOLDERS,
  FILES,
}

export enum ItemType {
  FOLDER,
  FILE,
}

export interface ChooseFileFormItem {
  path: string;
  type: ItemType;
}

interface ChooseFileFormState {
  directoryPath: string;                      // Path of the current directory
  directoryItems: DirectoryItem[];            // Items in the current directory
  nextFileCandidate: string;
  editingFileCandidate: boolean;
  fileCandidates: ChooseFileFormItem[];
  fileCandidatesValidation: "error" | null;
  newDirectoryInput: string;
  isAllDataLoaded: boolean;
}

interface ChooseFileFormBaseProps {
  source?: ChooseFileFormSourceType;
  chooseMode: ChooseMode;
  initialPath?: string;
  buttonText: string;
  canMultiSelect: boolean;
  handleChoose?: SingleHandleChoose;
  handleMultiChoose?: MultiHandleChoose;
}

export type SingleHandleChoose = (filePath: string, fileType: ItemType) => Promise<any>;

export type MultiHandleChoose = (files: ChooseFileFormItem[]) => Promise<any>;

type ChooseFileFormProps = ChooseFileFormBaseProps & WithApiProperties & InjectedIntlProps;

const getDirectoryItemsListDimensions = (parent: HTMLElement) => {
  const paddingLeft = parseFloat(getComputedStyle(parent).paddingLeft) || 0;
  const paddingRight = parseFloat(getComputedStyle(parent).paddingRight) || 0;
  return {
    width: parent.clientWidth - paddingLeft - paddingRight,
    height: 400,
  };
};

const DirectoryItemsListComponent = ControlRoomTable(new DirectoryItemsColumnFactory(),
    getDirectoryItemsListDimensions);

class ChooseFileFormComponent extends React.Component<ChooseFileFormProps, ChooseFileFormState> {
  _logger: Logger = Logger.getLogger("ChooseFileForm");

  private strategy: ChooseFileStrategy;

  constructor(props: ChooseFileFormProps) {
    super(props);

    this.strategy = this.createStrategy();
    this.state = {
      directoryPath: this.strategy.pathSeparator,
      directoryItems: [],
      nextFileCandidate: "",
      editingFileCandidate: false,
      fileCandidates: [{
        path: this.strategy.pathSeparator,
        type: ItemType.FOLDER,
      }],
      fileCandidatesValidation: null,
      newDirectoryInput: "",
      isAllDataLoaded: true,
    };
  }

  private createStrategy() {
    const {source, api} = this.props;

    switch (source) {
    case ChooseFileFormSourceType.LocalAndNetwork:
      return new ChooseLocalOrNetworkFileStrategy(api);
    case ChooseFileFormSourceType.Local:
    default:
      return new ChooseLocalFileStrategy(api);
    }
  }

  componentDidMount() {
    let initialPath = this.props.initialPath;
    const pathSeparator = this.strategy.pathSeparator;
    if (initialPath) {
      if (this.props.chooseMode === ChooseMode.FILES && initialPath.lastIndexOf(pathSeparator) > 0) {
        initialPath = initialPath.slice(0, initialPath.lastIndexOf(pathSeparator) + 1);
      }
      this.changeDirectory(initialPath);
    } else {
      this.changeDirectory(pathSeparator);
    }
  }

  changeDirectory = (directoryPath: string) => {
    this.setState({isAllDataLoaded: false});
    this._logger.debug('changeDirectory = (directoryPath: string) => {')
    this.strategy.browse(directoryPath).then((directoryItems) => {
      this._logger.debug('this.strategy.browse(directoryPath).then((directoryItems) => {')
      this.setState({isAllDataLoaded: true});

      directoryItems.sort((a, b) => {
        const upperA = a.fileName.toUpperCase();
        const upperB = b.fileName.toUpperCase();

        if (upperA === upperB) {
          return 0;
        }
        return (upperA > upperB ? 1 : -1);
      });

      directoryItems.sort((a, b) => {
        if (a.directory === b.directory) {
          return 0;
        }
        return a.directory ? -1 : 1;
      });

      this.setState({
        directoryItems,
        directoryPath,
        fileCandidates: [{
          path: directoryPath,
          type: ItemType.FOLDER,
        }],
        fileCandidatesValidation: null,
        editingFileCandidate: false,
      });
    }, () => {
      this._logger.debug('about to: this.setState({fileCandidatesValidation: "error"})');
      this.setState({
        fileCandidatesValidation: "error",
        editingFileCandidate: true,
      });
    });
  }

  generateBreadCrumb = () => {
    const breadCrumbElementsArray = this.state.directoryPath.split(/[\\|/]/);

    const breadCrumbElements = [];
    for (const element of breadCrumbElementsArray) {
      let breadCrumbElement = null;
      if (element && element.trim() !== "") {
        breadCrumbElement = (<Button bsStyle="link" bsSize="small"
                                     onClick={() => {
                                       this.handleBreadCrumbClick(element);
                                     }}>{element}</Button>);
        breadCrumbElements.push((<Breadcrumb.Item key={element} active> {breadCrumbElement} </Breadcrumb.Item>));
      }
    }

    return (
        <Breadcrumb>
          {breadCrumbElements}
        </Breadcrumb>
    );
  }

  private get firstFileCandidate() {
    return this.state.fileCandidates[0]?.path || "";
  }

  private get firstFileCandidateType() {
    return this.state.fileCandidates[0]?.type || ItemType.FOLDER;
  }

  handleBreadCrumbClick = (elementToNavigateTo) => {
    const pathSeparator = this.strategy.pathSeparator;
    const index = this.firstFileCandidate.indexOf(elementToNavigateTo);
    if (index >= 0) {
      this.changeDirectory(this.firstFileCandidate.substring(0, index + elementToNavigateTo.length) + pathSeparator);
    } else {
      // Go to root if directory is not found
      this.changeDirectory(pathSeparator);
    }
  }

  handleDoubleClick = (_event: React.MouseEvent<any>, index: number) => {
    const directoryItem = this.state.directoryItems[index];
    if (directoryItem.directory) {
      this.changeDirectory(directoryItem.absolutePath);
    }
  }

  handleRowSelect = (selectedItems: DirectoryItem[]) => {
    if (this.props.chooseMode === ChooseMode.FOLDERS) {
      return;
    }
    const fileCandidates = this.findFolderCandidates(selectedItems);

    this.setState({
      fileCandidates: fileCandidates.map(c => ({
        path: c.absolutePath,
        type: c.directory ? ItemType.FOLDER : ItemType.FILE,
      })),
      fileCandidatesValidation: null,
    });
  }

  findFolderCandidates = (selectedItems: DirectoryItem[]): DirectoryItem[] => {
    const contentRootCandidate = this.state.directoryPath; // Use current directory path if no items selected

    if (selectedItems?.length > 0) {
      return selectedItems;
    }

    return [{
              absolutePath: contentRootCandidate,
              directory: true,
              fileName: contentRootCandidate
            } as DirectoryItem];
  }

  handleChoose = () => {
    if (this.props.canMultiSelect) {
      this.props.handleMultiChoose(this.state.fileCandidates);
    } else {
      this.props.handleChoose(this.firstFileCandidate, this.firstFileCandidateType);
    }
  }

  handleContentRootInputChanged = (e) => {
    this.setState({
      nextFileCandidate: e.target.value,
      editingFileCandidate: true
    });
  }

  handleContentRootInputEnterPressed = (e) => {
    if (e.keyCode === 13) {
      this.changeDirectory(e.target.value);
      e.preventDefault();
      this.setState({
        editingFileCandidate: false,
      });
    }
  }

  render() {
    const {buttonText, intl, canMultiSelect} = this.props;
    this._logger.debug('001 render: this.formatLocation()')
    return (
        <div>
          {this.generateBreadCrumb()}
          <DirectoryItemsListComponent isPaged={false}
                                       intl={intl}
                                       data={this.state.directoryItems}
                                       isAllDataLoaded={this.state.isAllDataLoaded}
                                       loadData={() => undefined}
                                       onRowDoubleClick={this.handleDoubleClick}
                                       onRowSelect={this.handleRowSelect}
                                       canMultiSelect={canMultiSelect}
                                       noItemsFoundMessage={<FormattedMessage
                                           id="studio.settings.add-root-form.empty-folder"
                                           defaultMessage="Empty directory"/>}
          />

          <form>
            <FormGroup controlId="location" validationState={this.state.fileCandidatesValidation}>
              <ControlLabel><FormattedMessage id="studio.settings.add-root-form.form-header"
                                              defaultMessage="Location"/></ControlLabel>
              <FormControl
                  type="text"
                  value={this.state.editingFileCandidate ? this.state.nextFileCandidate : this.formatLocation()}
                  onChange={this.handleContentRootInputChanged}
                  onKeyDown={this.handleContentRootInputEnterPressed}
              />
            </FormGroup>
          </form>

          <Button onClick={this.handleChoose} block
                  disabled={this.state.fileCandidatesValidation != null || this.state.editingFileCandidate}>
            {buttonText}</Button>
        </div>
    );
  }

  private formatLocation() {
    this._logger.debug('002 private formatLocation() {   and   this.state.fileCandidates: ' +
                       this.state.fileCandidates.map(f => f.path).join("; "));
    return this.state.fileCandidates.map(f => f.path).join("; ");
  }
}

export const ChooseFileForm = injectIntl(WithApi(ChooseFileFormComponent));